/**
 * Alertes.
 *
 * Elles sont produites par les workers et les triggers, jamais par
 * l'interface : celle-ci ne fait que les lire et les écarter.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";

export type AlertSeverity = "info" | "warning" | "critical";

export type AlertType =
  | "cashflow_warning"
  | "overdue_payment"
  | "margin_anomaly"
  | "insurance_expiry"
  | "material_order_needed"
  | "permit_deadline"
  | "project_delay"
  | "quote_expiring"
  | "recurring_expense_due"
  /**
   * Le rappel du soir. Ne désigne aucune entité : c'est une invitation à
   * ouvrir la file, pas un défaut sur une ligne. Posée par
   * `post_evening_queue_alerts()`, une seule à la fois tant qu'elle n'est ni
   * lue ni écartée.
   */
  | "evening_queue"
  /**
   * Une machine ou un lot loués, à rendre dans les deux jours ou en retard.
   * Posée chaque matin par `post_equipment_rental_alerts()`, une par étape et
   * par date de retour ; refermée quand la location est réglée.
   */
  | "equipment_rental_due";

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  message: string;
  relatedEntityType: string | null;
  relatedEntityId: string | null;
  actionLabel: string | null;
  actionUrl: string | null;
  isRead: boolean;
  createdAt: string;
}

export interface AlertRepository {
  listActive(ctx: ExecutionContext): Promise<Alert[]>;
  dismiss(ctx: ExecutionContext, alertIds: string[]): Promise<number>;
  markRead(ctx: ExecutionContext, alertIds: string[]): Promise<number>;
}

export const dismissAlertsInput = z.object({
  confirmationId: z.string().optional(),
  alertIds: z.array(z.string().uuid()).min(1, "Aucune alerte sélectionnée"),
});

export type DismissAlertsInput = z.infer<typeof dismissAlertsInput>;

/** Ordre d'affichage : le critique d'abord, puis le plus récent. */
const SEVERITY_RANK: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export function sortAlerts(alerts: readonly Alert[]): Alert[] {
  return [...alerts].sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return bySeverity !== 0 ? bySeverity : b.createdAt.localeCompare(a.createdAt);
  });
}

/**
 * Le geste que propose une alerte, s'il y en a un.
 *
 * Il faut les deux moitiés : un libellé sans adresse ne mène nulle part, et une
 * adresse sans libellé ne dit pas où elle mène. Seul un chemin de l'application
 * passe : la table reste écrivable par les comptes de l'entreprise, et une
 * adresse externe ou `javascript:` ne doit pas devenir un bouton.
 */
export function alertAction(
  alert: Pick<Alert, "actionLabel" | "actionUrl">,
): { label: string; to: string } | null {
  const { actionLabel: label, actionUrl: to } = alert;
  if (!label || !to || !/^\/(?![/\\])/.test(to)) return null;
  return { label, to };
}

export function createAlertServices(repo: AlertRepository) {
  const listerAlertes = defineService<{ confirmationId?: string }, Alert[]>({
    name: "lister_alertes",
    level: ActionLevel.Read,
    handler: async (ctx) => sortAlerts(await repo.listActive(ctx)),
  });

  const ecarterAlertes = defineService<DismissAlertsInput, { dismissed: number }>({
    name: "ecarter_alertes",
    // Réversible : on pose un drapeau, la ligne reste en base.
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => ({
      dismissed: await repo.dismiss(ctx, input.alertIds),
    }),
  });

  const marquerAlertesLues = defineService<DismissAlertsInput, { read: number }>({
    name: "marquer_alertes_lues",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => ({
      read: await repo.markRead(ctx, input.alertIds),
    }),
  });

  return { listerAlertes, ecarterAlertes, marquerAlertesLues };
}

export type AlertServices = ReturnType<typeof createAlertServices>;
